/**
 * Helper Functions - Utility functions for formatting, calculations, etc.
 */

export const safeCoerceNumber = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  const num = Number(String(val).replace(/[^0-9.-]+/g, ''));
  return isNaN(num) ? 0 : num;
};

export const getRecentMonths = (n) => {
  const months = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
};

export const truncateLabel = (str, n = 12) => {
  return str.length > n ? str.slice(0, n) + '…' : str;
};

export const formatCurrencyShort = (num) => {
  if (Math.abs(num) >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toFixed(0);
};

export const getContrastColor = (hexOrRgb) => {
  let r, g, b;
  if (hexOrRgb.startsWith('#')) {
    const hex = hexOrRgb.slice(1);
    r = parseInt(hex.substr(0, 2), 16);
    g = parseInt(hex.substr(2, 2), 16);
    b = parseInt(hex.substr(4, 2), 16);
  } else if (hexOrRgb.startsWith('rgb')) {
    const match = hexOrRgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    r = parseInt(match[1]);
    g = parseInt(match[2]);
    b = parseInt(match[3]);
  } else {
    return '#fff';
  }
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000' : '#fff';
};

export const formatCurrency = (num, currency = 'GBP') => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  } catch {
    const symbols = { GBP: '£', USD: '$', EGP: 'EGP', SAR: 'SAR', AED: 'AED' };
    const symbol = symbols[currency] || '£';
    return `${symbol}${Math.abs(num).toFixed(0)}`;
  }
};

export const getCurrencySymbol = (currency = 'GBP') => {
  const symbols = { GBP: '£', USD: '$', EGP: 'EGP', SAR: 'SAR', AED: 'AED' };
  return symbols[currency] || '£';
};

/**
 * Color Utilities - Helper functions for dynamic theme generation
 */

// Helper to convert hex to RGB
const hexToRgb = (hex) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Helper to convert RGB to hex
const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

// Helper to adjust color brightness via interpolation
const adjustColor = (hex, factor) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  // Positive factor: Mix with White (255)
  // Negative factor: Mix with Black (0)
  const target = factor > 0 ? 255 : 0;
  const f = Math.abs(factor) / 100;
  
  const mix = (val) => Math.round(val + (target - val) * f);
  
  return rgbToHex(mix(rgb.r), mix(rgb.g), mix(rgb.b));
};

// Generate all required variants from a single base color
export const generateColorVariants = (baseHex) => {
  if (!baseHex || !baseHex.startsWith('#')) return null;
  
  // Refined factors to better match Tailwind's progression and original brand shades
  // Positive = Mix with white, Negative = Mix with black
  return {
    50:  adjustColor(baseHex, 98),
    100: adjustColor(baseHex, 95),
    200: adjustColor(baseHex, 85),
    300: adjustColor(baseHex, 70),
    400: adjustColor(baseHex, 50),
    500: adjustColor(baseHex, 25), 
    600: baseHex,                 
    700: adjustColor(baseHex, -15), 
    800: adjustColor(baseHex, -30),
    900: adjustColor(baseHex, -50),
    // Semantic aliases
    bg: baseHex,
    hover: adjustColor(baseHex, -8),
    border: adjustColor(baseHex, -15),
    ring: adjustColor(baseHex, 45),
    text: baseHex,
  };
};

export const toMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const monthLabel = (ym) => {
  const [year, month] = ym.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'long' });
};

export const isCountableOrderStatus = (status) => {
  return !['cancelled', 'returned', 'deleted'].includes((status || '').toLowerCase());
};

export const calcPerTickWidth = (chartWidth, tickCount) => {
  return chartWidth / tickCount;
};

export const exportJson = (data, filename) => {
  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const exportCsv = (data, filename) => {
  if (data.length === 0) return;

  const Papa = window.Papa;
  if (!Papa) return console.error('PapaParse library not loaded.');

  const csv = '\ufeff' + Papa.unparse(data, {
    delimiter: ',',
    quotes: true,
    quoteChar: '"',
    escapeChar: '"',
    header: true,
    newline: '\r\n'
  });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

